<template>
  <div class="activation-page d-flex align-items-center justify-content-center">
    <div class="activation-card">
      <div class="text-center mb-4">
        <img src="images/logo-white-shadow.svg" alt="Logo" class="activation-logo mb-2">
        <h1 class="activation-title">
          Activation du compte
        </h1>
      </div>

      <div v-if="activationComplete" class="text-center" role="status">
        <p>Votre compte est active. Vous pouvez maintenant vous connecter.</p>
        <b-button variant="primary" block class="mt-4" @click="goToLogin">
          Aller a la connexion
        </b-button>
      </div>

      <form v-else @submit.prevent="handleSubmit">
        <p class="activation-description">
          Definissez votre mot de passe pour terminer l'activation de votre compte.
        </p>

        <b-form-group label-for="activation-password" class="activation-label">
          <template #label>
            Mot de passe <span class="required-marker" aria-hidden="true">*</span>
            <span class="sr-only">requis</span>
          </template>
          <b-form-input id="activation-password" v-model="password" type="password" autocomplete="new-password"
                        required autofocus :disabled="isSubmitting" @input="clearError" />
        </b-form-group>

        <ul class="password-requirements" aria-label="Exigences du mot de passe">
          <li :class="{ 'text-success': passwordRequirements.length }">
            Au moins 8 caracteres
          </li>
          <li :class="{ 'text-success': passwordRequirements.uppercase }">
            Une lettre majuscule
          </li>
          <li :class="{ 'text-success': passwordRequirements.lowercase }">
            Une lettre minuscule
          </li>
          <li :class="{ 'text-success': passwordRequirements.digit }">
            Un chiffre
          </li>
          <li :class="{ 'text-success': passwordRequirements.special }">
            Un caractere special
          </li>
        </ul>

        <b-form-group label-for="activation-confirmation" class="activation-label">
          <template #label>
            Confirmation du mot de passe <span class="required-marker" aria-hidden="true">*</span>
            <span class="sr-only">requise</span>
          </template>
          <b-form-input id="activation-confirmation" v-model="confirmationPassword" type="password"
                        autocomplete="new-password" required :disabled="isSubmitting" @input="clearError" />
        </b-form-group>

        <div v-if="errorMessage" class="activation-error text-center" role="alert">
          {{ errorMessage }}
        </div>

        <b-button type="submit" variant="primary" block class="mt-4"
                  :disabled="isSubmitting || !isPasswordValid || !passwordsMatch">
          {{ isSubmitting ? 'Activation...' : 'Activer mon compte' }}
        </b-button>

        <b-button type="button" variant="link" block class="mt-2" :disabled="isSubmitting" @click="goToLogin">
          Retour a la connexion
        </b-button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axiosIns'
import {
  BButton,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'

export default {
  name: 'ActivateAccount',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
  },
  data() {
    return {
      confirmationPassword: '',
      errorMessage: null,
      activationComplete: false,
      activationRequest: {
        userId: null,
        token: null,
      },
      isSubmitting: false,
      password: '',
    }
  },
  computed: {
    passwordRequirements() {
      return {
        length: this.password.length >= 8,
        uppercase: /[A-Z]/.test(this.password),
        lowercase: /[a-z]/.test(this.password),
        digit: /\d/.test(this.password),
        special: /[^A-Za-z0-9]/.test(this.password),
      }
    },
    isPasswordValid() {
      return Object.values(this.passwordRequirements).every(Boolean)
    },
    passwordsMatch() {
      return this.password.length > 0 && this.password === this.confirmationPassword
    },
  },
  created() {
    const { userId, token } = this.$route.query
    this.activationRequest = {
      userId: Array.isArray(userId) ? userId[0] : userId,
      token: Array.isArray(token) ? token[0] : token,
    }

    if (userId || token) {
      this.$router.replace({ path: this.$route.path }).catch(() => { })
    }
  },
  methods: {
    clearError() {
      this.errorMessage = null
    },
    goToLogin() {
      this.$router.replace({ name: 'login' })
    },
    async handleSubmit() {
      this.errorMessage = null

      if (!this.activationRequest.userId || !this.activationRequest.token) {
        this.errorMessage = 'Ce lien d activation est incomplet ou invalide.'
        return
      }

      if (!this.isPasswordValid || !this.passwordsMatch) {
        this.errorMessage = 'Choisissez un mot de passe conforme et confirmez-le.'
        return
      }

      this.isSubmitting = true

      try {
        await axios.post('/Auth/ActivateAccount', {
          ...this.activationRequest,
          password: this.password,
          confirmationPassword: this.confirmationPassword,
        })
        await this.$router.replace({ name: 'login' })
        this.activationComplete = true
        this.password = ''
        this.confirmationPassword = ''
      } catch (error) {
        const serverMessage = error.response && error.response.data
          ? error.response.data.error
          : null

        switch (serverMessage) {
          case 'Invalid or expired account activation link.':
            this.errorMessage = 'Ce lien d activation est invalide, expire ou deja utilise.'
            break
          case 'Invalid account activation request.':
            this.errorMessage = 'Les informations d activation sont incompletes.'
            break
          case 'Unable to activate account with the supplied password.':
            this.errorMessage = 'Le compte ne peut pas etre active avec ce mot de passe.'
            break
          default:
            this.errorMessage = error.response
              ? 'Activation impossible. Verifiez le lien et reessayez.'
              : 'Une erreur reseau s est produite. Verifiez votre connexion.'
        }
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.activation-page {
  min-height: 100vh;
  padding: 1rem;
  color: white;
  background: #202329 url('/images/refuge.jpg') center / cover;
  position: relative;
}

.activation-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
}

.activation-card {
  width: min(100%, 430px);
  padding: 2rem 3rem;
  border-radius: 10px;
  background: rgba(46, 46, 46, 0.9);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  position: relative;
}

.activation-logo {
  max-width: 180px;
}

.activation-title {
  font-size: 1.5rem;
  font-weight: bold;
}

.activation-description {
  color: #e5e5e5;
}

.activation-label label {
  color: white !important;
}

.required-marker {
  color: #ff6b6b;
  font-weight: 800;
}

.password-requirements {
  padding-left: 1.25rem;
  color: #e5e5e5;
  font-size: 0.85rem;
}

.activation-error {
  color: #ffb3b3;
  font-size: 0.9rem;
  font-weight: 800;
}

@media (max-width: 480px) {
  .activation-card {
    padding: 1.5rem;
  }
}
</style>
